import Head from "next/head";
import {
  getStoryblokApi,
  StoryblokComponent,
  useStoryblokState,
} from "@storyblok/react";
import { sbBlok } from "../types/sb-types";
import YouTubePlaylist from "../components/YouTubePlaylist";
import { fetchYoutubeVideos } from "../utils/youtube-playlist";

const HighlightsPageHead = ({
  seo,
  isProd,
}: {
  seo: {
    title: string;
    description: string;
    og_title: string;
    og_description: string;
    twitter_title: string;
    twitter_description: string;
  };
  isProd: boolean;
}) => {
  return (
    <Head>
      {!isProd && <meta name="robots" content="noindex, nofollow" />}
      <title>{seo?.title}</title>
      <link rel="icon" href="/favicon.ico" />
      <meta
        name="viewport"
        content="initial-scale=1.0, width=device-width, maximum-scale=1.0, user-scalable=0"
        key="viewport"
      />
      <meta name="title" content={seo?.title} />
      <meta name="description" content={seo?.description} />
      <meta property="og:title" content={seo?.og_title} />
      <meta property="og:description" content={seo?.og_description} />
      <meta property="og:image" content="/images/next_tournament_s3.jpg" />
      <meta property="twitter:title" content={seo?.twitter_title} />
      <meta property="twitter:description" content={seo?.twitter_description} />
      <meta property="twitter:image" content="/images/next_tournament_s3.jpg" />
    </Head>
  );
};

export default function HighlightsPage({
  isProd,
  story,
  videos,
  videosTemplate,
  nextPageToken,
  playlistId,
}: any) {
  story = useStoryblokState(story);

  return (
    <>
      <HighlightsPageHead seo={story?.content?.seo} isProd={isProd} />
      {story?.content?.body?.map((c: sbBlok) => {
        if (c.component === "youtube_playlist" && videos && videos.length > 0) {
          return (
            <YouTubePlaylist
              key={c._uid}
              videos={videos}
              videosTemplate={videosTemplate}
              nextPageToken={nextPageToken}
              playlistId={playlistId}
            />
          );
        }
        return <StoryblokComponent key={c._uid} blok={c} />;
      })}
    </>
  );
}

export async function getStaticProps() {
  const sbParams = {
    version: process.env.VERCEL_ENV === "production" ? "published" : "draft",
    resolve_links: "url",
  };

  const storyblokApi = getStoryblokApi();
  const { data: storyData } = await storyblokApi.get(
    "cdn/stories/highlights",
    sbParams
  );

  const youTubePlaylistData = storyData.story.content.body.find(
    (body_item: any) => body_item.component === "youtube_playlist"
  );
  const youtubePlaylistID = youTubePlaylistData?.playlist_id || "";
  const youtubePlaylistTemplate = youTubePlaylistData?.videos_template;

  const youtubeResponse = await fetchYoutubeVideos(
    youtubePlaylistID,
    youtubePlaylistTemplate
  );

  return {
    props: {
      story: storyData ? storyData.story : false,
      isProd: process.env.VERCEL_ENV === "production",
      videos: youtubeResponse.videos,
      videosTemplate: youtubePlaylistTemplate,
      nextPageToken: youtubeResponse.nextPageToken || null,
      playlistId: youtubePlaylistID,
    },
    revalidate: 3600,
  };
}
